import React, { useState, useEffect } from 'react';
import './App.css';
import { db, auth } from './firebase-config';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc,
  updateDoc,
  query, 
  where,
  serverTimestamp 
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut
} from 'firebase/auth';

// Individual Dashboard Component (separate to use hooks properly)
const IndividualDashboard = ({ user, handleLogout }) => {
  const [myReferrals, setMyReferrals] = useState([]);
  const [rewardsAsFriend, setRewardsAsFriend] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    
    const loadReferrals = async () => {
      try {
        const referralsRef = collection(db, 'referrals');
        
        // Load referrals where I'm the referrer
        const myRefQuery = query(referralsRef, where('referrerId', '==', user.id));
        const myRefSnapshot = await getDocs(myRefQuery);
        const myRefs = myRefSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Load referrals where I'm the friend
        const friendRefQuery = query(referralsRef, where('friendEmail', '==', user.email));
        const friendRefSnapshot = await getDocs(friendRefQuery);
        const friendRefs = friendRefSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        setMyReferrals(myRefs);
        setRewardsAsFriend(friendRefs);
        setLoadingReferrals(false);
      } catch (error) {
        console.error('Error loading referrals:', error);
        setLoadingReferrals(false);
      }
    };
    
    loadReferrals();
  }, [user?.id, user?.email]);

  const totalEarnings = myReferrals.reduce((sum, ref) => sum + (ref.earnings || 0), 0);
  const activeReferrals = myReferrals.filter(r => r.status !== 'rejected').length;
  const referralLink = `${window.location.origin}?ref=${user?.referralCode}`;  

  if (loadingReferrals) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your referrals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.name}! 👋</h1>
            <p className="text-sm text-gray-500">Track your referrals and earnings</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Rewards You're Earning Section */}
        {rewardsAsFriend.length > 0 && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg p-8 mb-8 text-white">
            <h2 className="text-2xl font-bold mb-4">🎉 Rewards You're Earning</h2>
            <p className="text-green-100 mb-4">Your friends shared companies with you!</p>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 space-y-3">
              {rewardsAsFriend.map((referral) => (
                <div key={referral.id} className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{referral.companyName}</div>
                    <div className="text-sm text-green-100">Referred by {referral.referrerName}</div>
                  </div>
                  <div className="text-right">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      referral.status === 'joined' ? 'bg-white text-green-600' : 'bg-green-700 text-white'
                    }`}>
                      {referral.status === 'joined' ? `$${referral.earnings || 500} Earned!` : referral.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral Link Card */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg p-8 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Your Referral Link</h2>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-4">
            <code className="text-white text-sm sm:text-base break-all">{referralLink}</code>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(referralLink);
              alert('Link copied!');
            }}
            className="bg-white text-indigo-600 px-6 py-2 rounded-lg font-semibold hover:bg-indigo-50 transition"
          >
            📋 Copy Link
          </button>
          <p className="mt-4 text-blue-100 text-sm">
            Share this link with friends to earn $500 each when their company joins!
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-500 text-sm font-medium mb-2">Total Referrals</h3>
            <p className="text-3xl font-bold text-blue-600">{myReferrals.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-500 text-sm font-medium mb-2">Total Earned</h3>
            <p className="text-3xl font-bold text-green-600">${totalEarnings}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-gray-500 text-sm font-medium mb-2">Active Referrals</h3>
            <p className="text-3xl font-bold text-purple-600">{activeReferrals}</p>
          </div>
        </div>

        {/* Referrals Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Companies You Referred</h2>
          </div>
          {myReferrals.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <p className="mb-4">No referrals yet</p>
              <p className="text-sm">Share your referral link above to get started!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Friend</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earnings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {myReferrals.map((referral) => (
                    <tr key={referral.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{referral.companyName}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{referral.renewalMonth}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{referral.friendName || referral.friendEmail}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          referral.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                          referral.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                          referral.status === 'meeting' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">${referral.earnings || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [view, setView] = useState('landing');
  const [loading, setLoading] = useState(true);
  const [nonprofitData, setNonprofitData] = useState(null);
// Capture referral code from URL
const [referralCode, setReferralCode] = useState('');
const [referrerName, setReferrerName] = useState('');
const [referrerType, setReferrerType] = useState('');  

  // Check URL for referral code on mount
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');
  if (refCode) {
    setReferralCode(refCode);
    setView('signup');
    
    const loadReferrerInfo = async () => {
      try {
        // Check individuals first
        const usersRef = collection(db, 'users');
        const userQuery = query(usersRef, where('referralCode', '==', refCode));
        const userSnapshot = await getDocs(userQuery);
        
        if (!userSnapshot.empty) {
          const referrerData = userSnapshot.docs[0].data();
          console.log('FOUND INDIVIDUAL:', referrerData.name);
          setReferrerName(referrerData.name);
          setReferrerType('individual');
        } else {
          // Check nonprofits
          const nonprofitsRef = collection(db, 'nonprofits');
          const nonprofitQuery = query(nonprofitsRef, where('referralCode', '==', refCode));
          const nonprofitSnapshot = await getDocs(nonprofitQuery);
          
          if (!nonprofitSnapshot.empty) {
            const nonprofitData = nonprofitSnapshot.docs[0].data();
            console.log('FOUND NONPROFIT:', nonprofitData.orgName);
            setReferrerName(nonprofitData.orgName);
            setReferrerType('nonprofit');
          }
        }
      } catch (error) {
        console.error('Error loading referrer info:', error);
      }
    };
    
    loadReferrerInfo();
  }
}, []);

 // For nonprofit users

  // Form states
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    city: '',
    companyName: '',
    renewalMonth: '',
    userType: 'individual'
  });

  const [nonprofitAppData, setNonprofitAppData] = useState({
    orgName: '',
    ein: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    memberCount: '',
    mission: '',
    password: ''
  });

  // Referral form state
  const [referralData, setReferralData] = useState({
    companyName: '',
    renewalMonth: '',
    friendEmail: '',
    friendName: '',
  });

  const [showReferralForm, setShowReferralForm] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [allNonprofits, setAllNonprofits] = useState([]); // For admin
  const [allUsers, setAllUsers] = useState([]); // For admin

  // Helper functions
  const generateReferralCode = () => {
    return Math.random().toString(36).substring(2, 9).toUpperCase();
  };

  // Auth and data loading
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Load user data
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('uid', '==', firebaseUser.uid));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            const userWithId = {
              ...userData,
              id: querySnapshot.docs[0].id
            };
            setUser(userWithId);
            
            // Check if user is admin
            if (userData.email === 'admin@ourunitycommunity.org' || userData.isAdmin) {
              // Load all data for admin
              await loadAdminData();
              setView('admin');
            } else if (userData.userType === 'individual') {
              // Load individual's referrals
              const referralsRef = collection(db, 'referrals');
              const refQuery = query(referralsRef, where('referrerId', '==', querySnapshot.docs[0].id));
              const refSnapshot = await getDocs(refQuery);
              
              const userReferrals = [];
              refSnapshot.forEach((doc) => {
                userReferrals.push({ id: doc.id, ...doc.data() });
              });
              setReferrals(userReferrals);
              setView('dashboard');
            } else {
              // Load nonprofit data (with error handling for new signups)
              try {
                const nonprofitsRef = collection(db, 'nonprofits');
                const npQuery = query(nonprofitsRef, where('contactEmail', '==', userData.email));
                const npSnapshot = await getDocs(npQuery);
                
                if (!npSnapshot.empty) {
                  const npData = { id: npSnapshot.docs[0].id, ...npSnapshot.docs[0].data() };
                setNonprofitData(npData);
                
                if (npData.status === 'approved') {
                  // Load nonprofit's referrals
                  const referralsRef = collection(db, 'referrals');
                  const refQuery = query(referralsRef, where('nonprofitId', '==', npSnapshot.docs[0].id));
                  const refSnapshot = await getDocs(refQuery);
                  
                  const npReferrals = [];
                  refSnapshot.forEach((doc) => {
                    npReferrals.push({ id: doc.id, ...doc.data() });
                  });
                  setReferrals(npReferrals);
                  setView('nonprofitDashboard');
                } else {
                  setView('nonprofitPending');
                }
              } else {
                setView('nonprofitApp');
              }
              } catch (npError) {
                console.error('Error loading nonprofit data (may be new signup):', npError);
                // If error, assume they're a new nonprofit and show application
                setView('nonprofitApp');
              }
            }
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      } else {
        setUser(null);
        setReferrals([]);
        setView('landing');
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  const loadAdminData = async () => {
    try {
      // Load all nonprofits
      const nonprofitsSnapshot = await getDocs(collection(db, 'nonprofits'));
      const nonprofitsData = [];
      nonprofitsSnapshot.forEach((doc) => {
        nonprofitsData.push({ id: doc.id, ...doc.data() });
      });
      setAllNonprofits(nonprofitsData);

      // Load all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = [];
      usersSnapshot.forEach((doc) => {
        usersData.push({ id: doc.id, ...doc.data() });
      });
      setAllUsers(usersData);

      // Load all referrals
      const referralsSnapshot = await getDocs(collection(db, 'referrals'));
      const referralsData = [];
      referralsSnapshot.forEach((doc) => {
        referralsData.push({ id: doc.id, ...doc.data() });
      });
      setReferrals(referralsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Signup handler
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupData.email,
        signupData.password
      );
      
      const newUserReferralCode = generateReferralCode();
      await addDoc(collection(db, 'users'), {
        uid: userCredential.user.uid,
        name: signupData.name,
        email: signupData.email,
        city: signupData.city,
        companyName: signupData.companyName || '',
        renewalMonth: signupData.renewalMonth || '',
        userType: signupData.userType,
        referralCode: newUserReferralCode,
        createdAt: serverTimestamp()
      });

// If referralCode exists and company was provided, create referral
if (referralCode && signupData.companyName && signupData.renewalMonth) {
  console.log('Creating referral from referral code:', referralCode);
  
  // First, try to find individual referrer
  const usersRef = collection(db, 'users');
  const referrerQuery = query(usersRef, where('referralCode', '==', referralCode));
  const referrerSnapshot = await getDocs(referrerQuery);
  
  if (!referrerSnapshot.empty) {
    // INDIVIDUAL REFERRAL
    const referrerDoc = referrerSnapshot.docs[0];
    const referrerData = referrerDoc.data();
    
    console.log('Found individual referrer:', referrerData.name);
    
    await addDoc(collection(db, 'referrals'), {
      companyName: signupData.companyName,
      renewalMonth: signupData.renewalMonth,
      referrerId: referrerDoc.id,
      referrerName: referrerData.name,
      referrerEmail: referrerData.email,
      referrerType: 'individual',
      friendId: userCredential.user.uid,
      friendName: signupData.name,
      friendEmail: signupData.email,
      status: 'pending',
      earnings: 0,
      paymentAmount: 500,
      createdAt: serverTimestamp()
    });
    
    console.log('Individual referral created successfully');
  } else {
    // Try to find nonprofit referrer
    const nonprofitsRef = collection(db, 'nonprofits');
    const nonprofitQuery = query(nonprofitsRef, where('referralCode', '==', referralCode));
    const nonprofitSnapshot = await getDocs(nonprofitQuery);
    
    if (!nonprofitSnapshot.empty) {
      // NONPROFIT REFERRAL
      const nonprofitDoc = nonprofitSnapshot.docs[0];
      const nonprofitData = nonprofitDoc.data();
      
      console.log('Found nonprofit referrer:', nonprofitData.orgName);
      
      await addDoc(collection(db, 'referrals'), {
        companyName: signupData.companyName,
        renewalMonth: signupData.renewalMonth,
        nonprofitId: nonprofitDoc.id,
        nonprofitName: nonprofitData.orgName,
        referrerType: 'nonprofit',
        submittedBy: signupData.name,
        submittedByEmail: signupData.email,
        status: 'pending',
        earnings: 0,
        paymentAmount: 1000,
        createdAt: serverTimestamp()
      });
      
      console.log('Nonprofit referral created successfully');
    }
  }
}      
      setSignupData({
        name: '',
        email: '',
        password: '',
        city: '',
        companyName: '',
        renewalMonth: '',
        userType: 'individual'
      });
    } catch (error) {
      console.error('Signup error:', error);
      alert(error.message);
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(
        auth,
        signupData.email,
        signupData.password
      );
    } catch (error) {
      console.error('Login error:', error);
      alert('Invalid email or password');
      setLoading(false);
    }
  };

  // Nonprofit application handler
  const handleNonprofitApplication = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const referralCode = generateReferralCode();
      
      console.log('Step 1: Creating auth account...');
      // Step 1: Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        nonprofitAppData.contactEmail,
        nonprofitAppData.password
      );
      console.log('Step 1: SUCCESS - Auth account created');

      console.log('Step 2: Writing to users collection...');
      // Step 2: Write to users collection (just like individual signup)
      await addDoc(collection(db, 'users'), {
        uid: userCredential.user.uid,
        name: nonprofitAppData.contactName,
        email: nonprofitAppData.contactEmail,
        city: '',
        userType: 'nonprofit',
        referralCode,
        createdAt: serverTimestamp()
      });
      console.log('Step 2: SUCCESS - Users collection written');

      console.log('Step 3: Writing to nonprofits collection...');
      // Step 3: Write to nonprofits collection
      await addDoc(collection(db, 'nonprofits'), {
        uid: userCredential.user.uid,
        orgName: nonprofitAppData.orgName,
        ein: nonprofitAppData.ein || '',
        contactName: nonprofitAppData.contactName,
        contactEmail: nonprofitAppData.contactEmail,
        contactPhone: nonprofitAppData.contactPhone || '',
        memberCount: nonprofitAppData.memberCount,
        mission: nonprofitAppData.mission,
        status: 'pending',
        referralCode,
        createdAt: serverTimestamp()
      });
      console.log('Step 3: SUCCESS - Nonprofits collection written');

      // Clear form
      setNonprofitAppData({
        orgName: '',
        ein: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        memberCount: '',
        mission: ''
      });

      console.log('All steps completed successfully!');
      // Auth listener will handle redirect automatically
    } catch (error) {
      console.error('Application error at step:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      if (error.code === 'auth/email-already-in-use') {
        alert('This email is already registered. Please use the login page instead.');
      } else {
        alert('Error submitting application: ' + error.message);
      }
      setLoading(false);
    }
  };

  // Add referral handler
  const handleAddReferral = async (e) => {
    e.preventDefault();
    
    if (!referralData.companyName || !referralData.renewalMonth || !referralData.friendName || !referralData.friendEmail) {
      alert('Please fill out all fields');
      return;
    }
    
    try {
      const newReferral = {
        companyName: referralData.companyName,
        renewalMonth: referralData.renewalMonth,
        friendEmail: referralData.friendEmail,
        friendName: referralData.friendName,
        referrerId: user.id,
        referrerName: user.name,
        referrerEmail: user.email,
        status: 'pending',
        earnings: 0,
        createdAt: serverTimestamp()
      };

      if (nonprofitData) {
        newReferral.nonprofitId = nonprofitData.id;
        newReferral.nonprofitName = nonprofitData.orgName;
      }
      
      const docRef = await addDoc(collection(db, 'referrals'), newReferral);
      
      setReferrals([...referrals, { id: docRef.id, ...newReferral, createdAt: new Date() }]);
      
      setReferralData({
        companyName: '',
        renewalMonth: '',
        friendEmail: '',
        friendName: ''
      });
      setShowReferralForm(false);
      
      alert('Referral submitted successfully!');
    } catch (error) {
      console.error('Error adding referral:', error);
      alert('Error submitting referral. Please try again.');
    }
  };

  // Admin functions
  const approveNonprofit = async (nonprofitId) => {
    try {
      await updateDoc(doc(db, 'nonprofits', nonprofitId), {
        status: 'approved',
        approvedAt: serverTimestamp()
      });
      
      await loadAdminData();
      alert('Nonprofit approved! They will receive a notification.');
    } catch (error) {
      console.error('Error approving nonprofit:', error);
      alert('Error approving nonprofit');
    }
  };

  const rejectNonprofit = async (nonprofitId) => {
    if (window.confirm('Are you sure you want to reject this application?')) {
      try {
        await updateDoc(doc(db, 'nonprofits', nonprofitId), {
          status: 'rejected',
          rejectedAt: serverTimestamp()
        });
        
        await loadAdminData();
        alert('Application rejected.');
      } catch (error) {
        console.error('Error rejecting nonprofit:', error);
        alert('Error rejecting nonprofit');
      }
    }
  };

  const updateReferralStatus = async (referralId, newStatus) => {
    try {
      const updates = { status: newStatus };
      
      if (newStatus === 'joined') {
        updates.earnings = 500;
        updates.joinedAt = serverTimestamp();
      }
      
      await updateDoc(doc(db, 'referrals', referralId), updates);
      await loadAdminData();
    } catch (error) {
      console.error('Error updating referral:', error);
      alert('Error updating referral status');
    }
  };

  // CSV Export
  const exportToCSV = () => {
    try {
      const headers = [
        'Referral ID',
        'Company Name',
        'Renewal Month',
        'Friend Name',
        'Friend Email',
        'Referrer Name',
        'Referrer Email',
        'Status',
        'Earnings',
        'Date Submitted'
      ];

      const rows = referrals.map(ref => [
        ref.id,
        ref.companyName,
        ref.renewalMonth,
        ref.friendName || '',
        ref.friendEmail || '',
        ref.referrerName,
        ref.referrerEmail,
        ref.status,
        ref.earnings || 0,
        ref.createdAt ? new Date(ref.createdAt.seconds * 1000).toLocaleDateString() : new Date().toLocaleDateString()
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `OUC_Referrals_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting data');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setView('landing');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const calculateTotalEarnings = () => {
    return referrals.reduce((sum, ref) => sum + (ref.earnings || 0), 0);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // LANDING PAGE
  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-indigo-600">OurUnityCommunity</h1>
            <button
              onClick={() => setView('login')}
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Log In
            </button>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            $1M Anonymous Pledge
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Turn $1M into $500M for Our Community
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Help 1,000 mid-size companies discover self-funding benefits. When they keep profits instead of giving them to insurance carriers, everyone wins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                setSignupData({ ...signupData, userType: 'individual' });
                setView('signup');
              }}
              className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition flex items-center justify-center gap-2"
            >
              Sign Up as Individual →
            </button>
            <button
              onClick={() => setView('nonprofitApp')}
              className="bg-white text-indigo-600 border-2 border-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition"
            >
              Apply as Nonprofit
            </button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-3xl mb-4">👤</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Individuals</h3>
              <div className="space-y-4 text-gray-600">
                <p>✓ Get your unique referral link</p>
                <p>✓ Share with friends at mid-size companies</p>
                <p>✓ They submit company name + renewal month</p>
                <p>✓ You both earn $500 when company joins</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-3xl mb-4">🏫</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Nonprofits</h3>
              <div className="space-y-4 text-gray-600">
                <p>✓ Apply with your organization details</p>
                <p>✓ Get approved and receive your link</p>
                <p>✓ Members submit companies (no email needed)</p>
                <p>✓ Nonprofit earns $1,000 per company that joins</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-indigo-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-3xl font-bold mb-6">The Impact</h3>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="text-4xl font-bold mb-2">$1M</div>
                <div className="text-indigo-200">Community Pledge</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">1,000</div>
                <div className="text-indigo-200">Companies Helped</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">$500M</div>
                <div className="text-indigo-200">Kept in Community</div>
              </div>
            </div>
            <p className="text-xl text-indigo-100">
              When companies self-fund and keep their profits, that money stays in the business and the community.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-indigo-600 font-bold text-lg mb-2">PROVEN RESULTS</div>
            <div className="text-4xl font-bold text-gray-900 mb-4">95% Retention Rate</div>
            <p className="text-gray-600">
              Once companies join self-funding pools, 95% stay because it works.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // SIGNUP PAGE
  if (view === 'signup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Sign Up</h2>
{referralCode && referrerName && (
  <div className=" bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6">
    <p className="text-green-800 font-semibold text-center">
      🎉 {referrerName} invited you to Our Unity Community!
    </p>
    <p className="text-green-700 text-sm text-center mt-2">
      {referrerType === 'nonprofit' 
        ? `When your company joins, ${referrerName} earns $1,000 for their mission!`
        : `You and ${referrerName} will each earn $500 when your company joins!`
      }
    </p>
  </div>
)}
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
              <input
                type="text"
                value={signupData.name}
                onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">Email *</label>
              <input
                type="email"
                value={signupData.email}
                onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">Password *</label>
              <input
                type="password"
                value={signupData.password}
                onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">City *</label>
              <input
                type="text"
                value={signupData.city}
                onChange={(e) => setSignupData({ ...signupData, city: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-gray-500 mb-3">Optional: If you're also submitting your own company</p>
              
              <div className="mb-3">
                <label className="block text-gray-700 font-medium mb-2">Company Name (Optional)</label>
                <input
                  type="text"
                  value={signupData.companyName}
                  onChange={(e) => setSignupData({ ...signupData, companyName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Your company name"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Renewal Month (Optional)</label>
                <select
                  value={signupData.renewalMonth}
                  onChange={(e) => setSignupData({ ...signupData, renewalMonth: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select month...</option>
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          
          <button
            onClick={() => setView('landing')}
            className="w-full mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // LOGIN PAGE
  if (view === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Log In</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Email</label>
              <input
                type="email"
                value={signupData.email}
                onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">Password</label>
              <input
                type="password"
                value={signupData.password}
                onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
          
          <button
            onClick={() => setView('landing')}
            className="w-full mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // NONPROFIT APPLICATION PAGE
  if (view === 'nonprofitApp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-2xl w-full">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Nonprofit Application</h2>
          <p className="text-gray-600 mb-8">Tell us about your organization to get started</p>
          
          <form onSubmit={handleNonprofitApplication} className="space-y-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Organization Name *</label>
              <input
                type="text"
                value={nonprofitAppData.orgName}
                onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, orgName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="ABC Elementary PTA"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">EIN (Tax ID) - Optional</label>
              <input
                type="text"
                value={nonprofitAppData.ein}
                onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, ein: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="12-3456789"
              />
              <p className="text-sm text-gray-500 mt-1">💡 Don't have it handy? No problem - we'll collect this during approval.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Contact Name *</label>
                <input
                  type="text"
                  value={nonprofitAppData.contactName}
                  onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, contactName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Contact Email *</label>
                <input
                  type="email"
                  value={nonprofitAppData.contactEmail}
                  onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, contactEmail: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Password *</label>
                <input
                  type="password"
                  value={nonprofitAppData.password}
                  onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Create a secure password"
                  required
                 />
             </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Contact Phone</label>
                <input
                  type="tel"
                  value={nonprofitAppData.contactPhone}
                  onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, contactPhone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Estimated Member Count *</label>
                <input
                  type="text"
                  value={nonprofitAppData.memberCount}
                  onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, memberCount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="1000"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Mission Statement *</label>
              <textarea
                value={nonprofitAppData.mission}
                onChange={(e) => setNonprofitAppData({ ...nonprofitAppData, mission: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows="4"
                placeholder="Describe your organization's mission and how this partnership could help..."
                required
              />
            </div>

            <div className="bg-indigo-50 rounded-lg p-6">
              <h3 className="font-semibold text-indigo-900 mb-2">What Happens Next?</h3>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>✓ Your application will be reviewed within 2-3 business days</li>
                <li>✓ Once approved, you'll receive your nonprofit referral link</li>
                <li>✓ Your members can start submitting company referrals</li>
                <li>✓ Earn $1,000 for each company that joins self-funding</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
          
          <button
            onClick={() => setView('landing')}
            className="w-full mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // NONPROFIT PENDING APPROVAL
  if (view === 'nonprofitPending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-indigo-600">OurUnityCommunity</h1>
            <button onClick={handleLogout} className="text-gray-600 hover:text-gray-800">
              Logout
            </button>
          </div>
        </header>
        
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-xl shadow-lg p-12">
            <div className="text-6xl mb-6">⏳</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Under Review</h2>
            <p className="text-gray-600 mb-6">
              Thank you for applying! Your nonprofit application is currently being reviewed by our team.
              You'll receive an email notification once your application has been approved.
            </p>
            {nonprofitData && (
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="font-semibold text-indigo-900 mb-2">Application Details:</h3>
                <div className="text-left space-y-2 text-gray-700">
                  <p><strong>Organization:</strong> {nonprofitData.orgName}</p>
                  <p><strong>Contact:</strong> {nonprofitData.contactName}</p>
                  <p><strong>Member Count:</strong> {nonprofitData.memberCount}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // INDIVIDUAL DASHBOARD
  if (view === 'dashboard' && user) {
    return <IndividualDashboard user={user} handleLogout={handleLogout} />;
  }

  // NONPROFIT DASHBOARD
  if (view === 'nonprofitDashboard' && nonprofitData) {
    const totalEarnings = calculateTotalEarnings();
    const activeReferrals = referrals.filter(r => r.status !== 'rejected').length;
    const referralLink = `${window.location.origin}?ref=${nonprofitData.referralCode}`;
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-indigo-600">{nonprofitData.orgName}</h1>
              <p className="text-sm text-gray-500">Track member referrals and donations</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
            >
              Logout
            </button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Referral Link Card */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg p-8 mb-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Your Nonprofit Referral Link</h2>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-4">
              <code className="text-white text-sm sm:text-base break-all">
                {referralLink}
              </code>
            </div>
            <button
              onClick={() => copyToClipboard(referralLink)}
              className="bg-white text-indigo-600 px-6 py-2 rounded-lg font-semibold hover:bg-indigo-50 transition"
            >
              {copySuccess ? '✓ Copied!' : '📋 Copy Link'}
            </button>
            <p className="mt-4 text-indigo-100 text-sm">
              Share this link with your members. When they submit companies that join, your nonprofit earns $1,000 per company!
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Total Referrals</h3>
              <p className="text-3xl font-bold text-blue-600">{referrals.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Total Earned</h3>
              <p className="text-3xl font-bold text-green-600">${totalEarnings}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Pending Payment</h3>
              <p className="text-3xl font-bold text-orange-600">$0</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Active Referrals</h3>
              <p className="text-3xl font-bold text-purple-600">{activeReferrals}</p>
            </div>
          </div>

          {/* Member Referrals Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Member Referrals</h2>
            </div>

            {referrals.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <p className="mb-4">No member referrals yet</p>
                <p className="text-sm">Share your referral link with members to get started!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earnings</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {referrals.map((referral) => (
                      <tr key={referral.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{referral.companyName}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{referral.renewalMonth}</td>
                        <td className="px-6 py-4">
                          <span className={
                            referral.status === 'pending' ? 'px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800' :
                            referral.status === 'contacted' ? 'px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800' :
                            referral.status === 'meeting' ? 'px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800' :
                            referral.status === 'joined' ? 'px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800' :
                            'px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800'
                          }>
                            {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">${referral.earnings || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ADMIN DASHBOARD
  if (view === 'admin') {
    const pendingNonprofits = allNonprofits.filter(np => np.status === 'pending');
    const approvedNonprofits = allNonprofits.filter(np => np.status === 'approved');

    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-indigo-600">Admin Dashboard</h1>
            <div className="flex gap-3">
              <button
                onClick={exportToCSV}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition"
              >
                📊 Export CSV
              </button>
              <button onClick={handleLogout} className="text-gray-600 hover:text-gray-800">
                Logout
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Total Users</h3>
              <p className="text-3xl font-bold text-blue-600">{allUsers.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Approved Nonprofits</h3>
              <p className="text-3xl font-bold text-green-600">{approvedNonprofits.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Pending Applications</h3>
              <p className="text-3xl font-bold text-orange-600">{pendingNonprofits.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-500 text-sm font-medium mb-2">Total Referrals</h3>
              <p className="text-3xl font-bold text-purple-600">{referrals.length}</p>
            </div>
          </div>

          {/* Pending Nonprofit Applications */}
          {pendingNonprofits.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Pending Nonprofit Applications</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {pendingNonprofits.map((nonprofit) => (
                  <div key={nonprofit.id} className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{nonprofit.orgName}</h3>
                        <p className="text-sm text-gray-500">EIN: {nonprofit.ein || 'Not provided'}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => approveNonprofit(nonprofit.id)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => rejectNonprofit(nonprofit.id)}
                          className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600"><strong>Contact:</strong> {nonprofit.contactName}</p>
                        <p className="text-gray-600"><strong>Email:</strong> {nonprofit.contactEmail}</p>
                        <p className="text-gray-600"><strong>Phone:</strong> {nonprofit.contactPhone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600"><strong>Member Count:</strong> {nonprofit.memberCount}</p>
                        <p className="text-gray-600"><strong>Mission:</strong> {nonprofit.mission}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Referrals */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">All Referrals</h2>
            </div>
            {referrals.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                No referrals submitted yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {referrals.map((referral) => (
                      <tr key={referral.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{referral.companyName}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{referral.referrerName}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{referral.renewalMonth}</td>
                        <td className="px-6 py-4">
                          <select
                            value={referral.status}
                            onChange={(e) => updateReferralStatus(referral.id, e.target.value)}
                            className="text-xs font-semibold rounded-full px-3 py-1 border-2 focus:outline-none"
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="meeting">Meeting</option>
                            <option value="joined">Joined</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">${referral.earnings || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default App;
